import React, { useState, useEffect, useRef } from 'react';
import './BildeGalleri.css';

/**
 * Bildegalleri - Viser opplastede bilder med mulighet for sletting
 *
 * Props:
 * - bilder: Array av bilde-objekter
 * - onDelete: Callback når bilde slettes
 * - onRefresh: Callback for å refreshe bildeliste
 *
 * OPTIMALISERING:
 * - Lazy loading av bilder (Intersection Observer API)
 * - Progressive loading (lav oppløsning først, deretter full)
 * - Reduced data transfer (kun synlige bilder lastes)
 */
function BildeGalleri({ bilder = [], onDelete, onRefresh }) {
  const [sletter, setSletter] = useState({});
  const [visningsModusBilde, setVisningsModusBilde] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const observerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    // Cleanup existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const bildeId = img.dataset.bildeId;

            if (!loadedImages.has(bildeId)) {
              // Load full image
              const fullSrc = img.dataset.src;
              img.src = fullSrc;
              img.classList.add('loaded');

              setLoadedImages(prev => new Set([...prev, bildeId]));

              // Stop observing this image
              observerRef.current.unobserve(img);
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01
      }
    );

    // Observe all lazy images
    const images = document.querySelectorAll('.bilde-thumbnail[data-src]');
    images.forEach(img => {
      observerRef.current.observe(img);
    });

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [bilder, loadedImages]);

  if (!bilder || bilder.length === 0) {
    return (
      <div className="ingen-bilder">
        <p>Ingen bilder lastet opp ennå</p>
      </div>
    );
  }

  const handleSlett = async (bildeId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette bildet?')) {
      return;
    }

    setSletter({ ...sletter, [bildeId]: true });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';

      // Hent token fra localStorage
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/bilder/${bildeId}`, {
        method: 'DELETE',
        headers: headers
      });

      if (!response.ok) {
        throw new Error('Kunne ikke slette bilde');
      }

      // Callback
      if (onDelete) {
        onDelete(bildeId);
      }
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Slett bilde error:', error);
      alert('Feil ved sletting av bilde: ' + error.message);
    } finally {
      setSletter({ ...sletter, [bildeId]: false });
    }
  };

  return (
    <div className="bilde-galleri">
      <div className="bilder-grid">
        {bilder.map((bilde) => (
          <div key={bilde.id} className="bilde-item">
            <div className="bilde-wrapper">
              <img
                src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23e0e0e0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='18' fill='%23999'%3ELaster...%3C/text%3E%3C/svg%3E"
                data-src={bilde.cloudflare_r2_url}
                data-bilde-id={bilde.id}
                alt={bilde.filnavn}
                className="bilde-thumbnail"
                onClick={() => setVisningsModusBilde(bilde)}
                loading="lazy"
              />
              <button
                className="slett-knapp"
                onClick={() => handleSlett(bilde.id)}
                disabled={sletter[bilde.id]}
                title="Slett bilde"
              >
                {sletter[bilde.id] ? '⏳' : '🗑️'}
              </button>
            </div>
            {bilde.bildetekst && (
              <p className="bildetekst">{bilde.bildetekst}</p>
            )}
            {bilde.beskrivelse && (
              <p className="bildebeskrivelse">{bilde.beskrivelse}</p>
            )}
            <p className="bilde-metadata">
              {bilde.filnavn} • {Math.round(bilde.filstorrelse / 1024)} KB
            </p>
          </div>
        ))}
      </div>

      {/* Fullscreen bilde-visning */}
      {visningsModusBilde && (
        <div className="bilde-modal" onClick={() => setVisningsModusBilde(null)}>
          <div className="bilde-modal-innhold">
            <button
              className="lukk-modal"
              onClick={() => setVisningsModusBilde(null)}
            >
              ✕
            </button>
            <img
              src={visningsModusBilde.cloudflare_r2_url}
              alt={visningsModusBilde.filnavn}
              className="bilde-fullscreen"
            />
            {visningsModusBilde.bildetekst && (
              <p className="modal-bildetekst">{visningsModusBilde.bildetekst}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BildeGalleri;
